import { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import MusicListField from './MusicListField';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    Button,
    CircularProgress,
    Container,
    Grid,
    Paper,
    Typography,
    IconButton,
    FormControlLabel,
    Checkbox,
    Box,
    Alert,
} from '@mui/material';
import { useAddNewEventMutation, useEditEventMutation } from '../../../../api/eventsApiSlice';
import { useDeleteImagesMutation } from '../../../../api/imagesApiSlice';
import { selectAllEventTypes } from '../../../../api/eventTypesApiSlice';
import { selectAllAudiences } from '../../../../api/audiencesApiSlice';
import { selectAllPartners } from '../../../../api/partnersApiSlice';
import { selectAllPeopleNames } from '../../../../api/peopleApiSlice';
import { selectAllRoles } from '../../../../api/rolesApiSlice';
import { selectAllCenters } from '../../../../api/centersApiSlice';
import AddIcon from '@mui/icons-material/Add';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import ProgressLoader from '../../../../components/ProgressLoader';
import MessageSnackbar from '../../../../components/MessageSnackbar';
import FormDatePicker from '../../../../components/FormFields/FormDatePicker';
import FormTextField from '../../../../components/FormFields/FormTextField';
import FormListField from './FormListField';
import FormListSelect from './FormListSelect';
import FormSelectMultiple from './FormSelectMultiple';
import FormTimePicker from './FormTimePicker';
import FormRadioGroup from './FormRadioGroup';
import FormNotes from './FormNotes';
import ImageUploader from './ImageUploader/ImageUploader';
import Places from './Places/Places';
import Schedule from './Schedule';
import { useGetPlacesQuery } from '../../../../api/placesApiSlice';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    validationSchema,
    defaultValues,
    convertDataToAPISchema,
    convertDataToFormSchema,
} from './eventFormSchemas';
import FormCheckbox from './FormCheckbox';
import MusicSelect from '../MusicSelect';
import Vastuuhenkilöt from '../VastuuhenkilötSelect';
import YhteistyotahotForm from './YhteistyötahoLisäysComponent';
import HenkilotAddForm from './henkilöLisäysComponent';
import MukanaOlevat from './MukanaOlevat';
import RichTextEditor from '../../../../components/RichTextEditor';
import debounce from 'lodash/debounce';
import HelpIconWithTooltip from '../../../../components/HelpIconWithTooltip';
import { useAddNewLokiMutation } from '../../../../api/lokiSlice';
import { selectCurrentUser } from '../../../authentication/authSlice';
import FormCheckboxGroup from './FormCheckboxGroup';

const EventForm = ({ event, isEditing, documentTitle }) => {
    useDocumentTitle(documentTitle);

    const copiedSessionValues = sessionStorage.getItem('copiedSessionValues');
    function tryParse(input) {
        try {
            return JSON.parse(input);
        } catch (e) {
            return undefined;
        }
    }
    const parsedCopiedValues = useMemo(() => tryParse(copiedSessionValues), [copiedSessionValues]);
    const [successMessage, setSuccessMessage] = useState('');
    const [showWarning, setShowWarning] = useState(false);
    const warningMessage =
        'Huomio: Et voi nähdä toisen ihmisen luomaa tapahtumaa, jonka tallennat luonnokseksi, jos et ole admin tai muokkaaja';
    const [errorMessage, setErrorMessage] = useState('');
    const [henkiloSuccessMessage, setHenkiloSuccessMessage] = useState('');
    const [henkiloErrorMessage, setHenkiloErrorMessage] = useState('');
    const [initialFormValues, setInitialFormValues] = useState(
        (isEditing || parsedCopiedValues) ? null : defaultValues
    );
    const [hasCompletedInitialHydration, setHasCompletedInitialHydration] = useState(
        !(isEditing || parsedCopiedValues)
    );
    const [eventLength, setEventLength] = useState(0);

    const handleSuccessfulCreation = (message) => setSuccessMessage(message);
    const handleError = (message) => setErrorMessage(message);

    const [{ submitting, submitted, error, message }, setFormStatus] = useState({
        submitting: false,
        submitted: false,
        error: false,
        message: '',
    });

    const eventTypes = useSelector(selectAllEventTypes);
    const audiences = useSelector(selectAllAudiences);
    const partners = useSelector(selectAllPartners);
    const roles = useSelector(selectAllRoles);
    const people = useSelector(selectAllPeopleNames);
    const centers = useSelector(selectAllCenters);
    const { data: places } = useGetPlacesQuery();
    let placesArray = places ? Object.values(places?.entities) : [];

    const [mutationToUse] = isEditing ? useEditEventMutation() : useAddNewEventMutation();
    const [deleteImagesFromDisk] = useDeleteImagesMutation();
    const navigate = useNavigate();

    const handleCancelCopy = () => {
        sessionStorage.removeItem('copiedSessionValues');
        navigate('/tapahtumat');
    };

    useEffect(() => {
        if (event || parsedCopiedValues) {
            const convertedData = convertDataToFormSchema(event || parsedCopiedValues);
            setInitialFormValues(convertedData);
            setHasCompletedInitialHydration(false);
        } else if (!isEditing) {
            setInitialFormValues(defaultValues);
            setHasCompletedInitialHydration(true);
        }
    }, [event, isEditing, parsedCopiedValues]);

    const { formState, ...methods } = useForm({
        resolver: (data, context, options) =>
            yupResolver(validationSchema)(data, { ...data, isEditing }, options),
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: parsedCopiedValues || defaultValues,
        values: initialFormValues ?? defaultValues,
        resetOptions: { keepDefaultValues: true },
    });
    const { errors } = formState;

    const [kansanopistokurssi, setKansanopistoKurssi] = useState(
        initialFormValues?.kansanopistokurssi || false
    );
    const [kuvausValue, setKuvausValue] = useState(initialFormValues?.kuvaus || '');
    const [lisatiedotValue, setLisatiedotValue] = useState(initialFormValues?.lisatiedot || '');
    const debouncedHandleKuvausChange = debounce((fieldName, value) => {
        setKuvausValue(value);
    }, 100);
    const debouncedHandleLisatiedotChange = debounce((fieldName, value) => {
        setLisatiedotValue(value);
    }, 100);
    const handleKuvausChange = (fieldName, value) => debouncedHandleKuvausChange(fieldName, value);
    const handleLisatiedotChange = (fieldName, value) =>
        debouncedHandleLisatiedotChange(fieldName, value);

    useEffect(() => {
        setKansanopistoKurssi(initialFormValues?.kansanopistokurssi || false);
    }, [initialFormValues?.kansanopistokurssi]);
    useEffect(() => {
        setKuvausValue(initialFormValues?.kuvaus || '');
    }, [initialFormValues?.kuvaus]);

    const { name } = useSelector(selectCurrentUser);

    const calculateTotalHours = () => {
        let totalHours = 0;
        (initialFormValues?.ohjelma || []).forEach((day) => {
            if (day.tilaisuudet && Array.isArray(day.tilaisuudet)) {
                day.tilaisuudet.forEach((tilaisuus) => {
                    if (tilaisuus.kansanopistotunnit && Array.isArray(tilaisuus.kansanopistotunnit)) {
                        tilaisuus.kansanopistotunnit.forEach((hoursObj) => {
                            totalHours += hoursObj.kansanopistotunnit || 0;
                        });
                    }
                });
            }
        });
        return totalHours;
    };

    const [hours, setHours] = useState(0);
    const [sendLog] = useAddNewLokiMutation();
    useEffect(() => {
        const newTotalHours = calculateTotalHours();
        setHours(newTotalHours);
    }, [initialFormValues]);

    const startTime = methods.watch('aloitus.pvm');
    const endTime = methods.watch('lopetus.pvm');
    const ohjelma = methods.watch('ohjelma');
    const paikkaNimiValue = methods.watch('paikka.nimi');

    const firstRender = useRef(true);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (
            startTime &&
            startTime !== initialFormValues?.aloitus?.pvm &&
            startTime !== methods.getValues('lopetus.pvm')
        ) {
            methods.setValue('lopetus.pvm', startTime);
        }
    }, [startTime]);

    const aloitusAikaValue = methods.watch('aloitus.aika');
    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        if (
            aloitusAikaValue &&
            aloitusAikaValue !== initialFormValues?.aloitus?.aika &&
            aloitusAikaValue !== methods.getValues('lopetus.aika')
        ) {
            methods.setValue('lopetus.aika', aloitusAikaValue);
        }
    }, [aloitusAikaValue]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            setFormStatus((prev) => ({
                ...prev,
                submitting: false,
                error: true,
                message: 'Virhe: Lomakkeen tiedoista puuttuu jotain tai niissä on virheitä.',
            }));}
    }, [errors]);

    const status = methods.watch('status');
    const nimiValue = methods.watch('nimi');

    const isWaitingForInitialValues = Boolean((isEditing || parsedCopiedValues) && !initialFormValues);

    const getComparableDateValue = (value) => {
        if (!value)
            return '';

        const parsedValue = dayjs(value);
        return parsedValue.isValid() ? parsedValue.valueOf() : String(value);
    };

    const isWaitingForHydratedFormValues = Boolean(
        initialFormValues &&
        !hasCompletedInitialHydration &&
        (isEditing || parsedCopiedValues) &&
        (
            nimiValue !== (initialFormValues.nimi || '') ||
            status !== (initialFormValues.status || '') ||
            paikkaNimiValue !== (initialFormValues.paikka?.nimi || '') ||
            getComparableDateValue(startTime) !== getComparableDateValue(initialFormValues.aloitus?.pvm) ||
            getComparableDateValue(endTime) !== getComparableDateValue(initialFormValues.lopetus?.pvm) ||
            (Array.isArray(ohjelma) ? ohjelma.length : 0) !== (initialFormValues.ohjelma?.length || 0)
        )
    );

    useEffect(() => {
        if (
            initialFormValues &&
            !hasCompletedInitialHydration &&
            !isWaitingForHydratedFormValues
        ) {
            setHasCompletedInitialHydration(true);
        }
    }, [initialFormValues, hasCompletedInitialHydration, isWaitingForHydratedFormValues]);

    const onSubmit = async (data) => {
        if (kansanopistokurssi && status !== 'luonnos') {
            if (eventLength < 2 || hours < eventLength * 5) {
                setFormStatus((prev) => ({
                    ...prev,
                    submitting: false,
                    error: true,
                    message: 'Virhe: Kansanopistokurssi vaatii vähintään 2 päivää ja riittävästi tunteja.',
                }));
                return;
            }
        }

        data.lastEditedTimestamp = Date.now();
        setFormStatus((prev) => ({ ...prev, submitting: true, error: false }));
        const convertedData = convertDataToAPISchema(data);

        try {
            const res = await mutationToUse(convertedData).unwrap();
            setFormStatus((prev) => ({
                ...prev,
                submitting: false,
                submitted: true,
                message: isEditing ? 'Muutokset tallennettu' : 'Tapahtuma luotu',
            }));
            setTimeout(() => navigate(`/tapahtumat/${res.id}`), 1000);
        } catch (err) {
            const uploadError = err?.data?.error || err?.data?.message;
            const isOversizedImageError = typeof uploadError === 'string' && uploadError.toLowerCase().includes('liian suuri');

            setFormStatus((prev) => ({
                ...prev,
                submitting: false,
                error: true,
                message: isOversizedImageError
                    ? `${uploadError} Pienennä kuvaa tai valitse valmiiksi ladattu kuva kuvakirjastosta.`
                    : `Virhe: tapahtuman ${
                        isEditing ? 'muokkaaminen' : 'tallentaminen'
                    } epäonnistui. Jos virhe toistuu, ota yhteyttä IT-tukeen.${
                        err?.status && ` [status: ${err.status}]`
                    }`,
            }));
        }
    };

    useEffect(() => {
        if (startTime && endTime && dayjs(startTime).isValid() && dayjs(endTime).isValid()) {
            const start = dayjs(startTime);
            const end = dayjs(endTime);
            const diff = end.diff(start, 'day');
            const length = diff < 0 ? 0 : diff === 0 ? 1 : diff + 1;
            setEventLength(length);
        } else {
            setEventLength(0);
        }
    }, [startTime, endTime]);

    useEffect(() => {
        if (
            status === 'luonnos' &&
            name !== event?.user?.name &&
            event?.user?.name !== '' &&
            event
        ) {
            setShowWarning(true);
        } else {
            setShowWarning(false);
        }
    }, [status, name, event?.user?.name]);

    const renderLoadingFormShell = (loadingMessage) => (
        <Container
            maxWidth="md"
            sx={{
                mt: { xs: 1, sm: 2 },
                px: { xs: 1, sm: 2 },
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    borderRadius: 8,
                    my: { xs: 2, sm: 3 },
                    px: { xs: 2, sm: 4, md: 8 },
                    py: { xs: 2, sm: 4 },
                    minHeight: { xs: 420, sm: 520 },
                }}
            >
                <Grid container rowSpacing={{ xs: 3, sm: 5, md: 7 }} columnSpacing={2}>
                    <Grid item xs={12} sx={{ pb: { xs: 3, sm: 5 } }}>
                        <Typography align="center" variant="h4">
                            {parsedCopiedValues
                                ? `${parsedCopiedValues?.nimi || 'Tapahtuma'} (kopio)`
                                : isEditing
                                    ? event?.nimi || 'Muokkaa tapahtumaa'
                                    : 'Uusi tapahtuma'}
                        </Typography>
                        {parsedCopiedValues && (
                            <>
                                <Typography
                                    align="center"
                                    variant="subtitle1"
                                    color="text.secondary"
                                    sx={{ mt: { xs: 1, sm: 3 } }}
                                >
                                    Muokkaat kopiota
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                    <Button variant="outlined" onClick={handleCancelCopy}>
                                        Peru kopiointi
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                textAlign: 'center',
                                pt: { xs: 3, sm: 5 },
                                minHeight: { xs: 240, sm: 300 },
                            }}
                        >
                            <ProgressLoader msg={loadingMessage} />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );

    if (
        !(
            eventTypes.length &&
            audiences.length &&
            partners.length &&
            roles.length &&
            people.length &&
            centers.length
        )
    )
        return renderLoadingFormShell('Ladataan tietoja...');

    if (isWaitingForInitialValues || isWaitingForHydratedFormValues)
        return renderLoadingFormShell(
            isEditing ? 'Ladataan tapahtuman tietoja...' : 'Ladataan kopioitavan tapahtuman tietoja...'
        );

    return (
        <Container
            maxWidth="md" // Original maxWidth restored
            sx={{
                mt: { xs: 1, sm: 2 }, // Responsive top margin
                px: { xs: 1, sm: 2 }, // Responsive horizontal padding
            }}
        >
            <Button
                title="Esikatsele"
                type="submit"
                form="event-form"
                variant="contained"
                disabled={submitting || submitted}
                sx={{
                    position: 'fixed',
                    right: { xs: '10px', sm: '20px' }, // Responsive positioning
                    bottom: { xs: '10px', sm: '40px' }, // Responsive positioning
                    zIndex: 1000,
                    minWidth: { xs: '40px', sm: 'auto' }, // Smaller on mobile
                    p: { xs: 1, sm: 'auto' }, // Reduced padding on mobile
                }}
            >
                <VisibilityIcon fontSize="small" />
            </Button>
            <FormProvider {...methods} formState={formState} defaultValues={defaultValues}>
                <Paper
                    id="event-form"
                    component="form"
                    onSubmit={methods.handleSubmit(onSubmit)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') e.preventDefault();
                    }}
                    elevation={3}
                    sx={{
                        borderRadius: 8,
                        my: { xs: 2, sm: 3 }, // Responsive margin
                        px: { xs: 2, sm: 4, md: 8 }, // Original px: 8 on md+
                        py: { xs: 2, sm: 4 }, // Original py: 4 on sm+
                    }}
                >
                    <Grid container rowSpacing={{ xs: 3, sm: 5, md: 7 }} columnSpacing={2}>
                        <Grid item xs={12} sx={{ pb: { xs: 3, sm: 5 } }}>
                            <Typography align="center" variant="h4">
                                {parsedCopiedValues
                                    ? `${nimiValue} (kopio)`
                                    : nimiValue || 'Uusi tapahtuma'}
                            </Typography>
                            {parsedCopiedValues && (
                                <>
                                    <Typography
                                        align="center"
                                        variant="subtitle1"
                                        color="text.secondary"
                                        sx={{ mt: { xs: 1, sm: 3 } }} // Original mt: 3 on sm+
                                    >
                                        Muokkaat kopiota
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                        <Button variant="outlined" onClick={handleCancelCopy}>
                                            Peru kopiointi
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </Grid>

                        {/* Nimi */}
                        <Grid item xs={12} md={4} sx={{ pt: { xs: 0, md: 1 } }}>
                            <Typography variant="h6">
                                Tapahtuman nimi{' '}
                                <HelpIconWithTooltip
                                    iconSize={20}
                                    tooltipFontSize="16px"
                                    text="Kirjoita tähän tapahtuman nimi"
                                />
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormTextField fieldName="nimi" label="Tapahtuman nimi" fullWidth />
                        </Grid>

                        {/* Luonne */}
                        <Grid item xs={12} md={4} sx={{ pt: { xs: 0, md: 1 } }}>
                            <Typography variant="h6">
                                Tapahtuman luonne{' '}
                                <HelpIconWithTooltip
                                    text="Luonteet kertovat mistä tapahtumassa on pääpiirteittäin kyse"
                                    iconSize={20}
                                    tooltipFontSize="16px"
                                />
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormSelectMultiple
                                fieldName="luonteet"
                                options={eventTypes}
                                label="Tapahtumaluonteet"
                            />
                        </Grid>

                        {/* Tapahtuman tyyppi */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman tyyppi</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormCheckbox
                                fieldName="kansanopistokurssi"
                                label="Kansanopistokurssi"
                                onChange={(value) => setKansanopistoKurssi(value)}
                            />
                        </Grid>
                        {kansanopistokurssi && (
                            <Grid item xs={12} sx={{ textAlign: 'center', py: 2 }}>
                                <Typography variant="h6">Kansanopistotunnit</Typography>
                                {startTime && endTime && (
                                    <>
                                        <Typography variant="body1">
                                            Yhteensä {hours} tuntia, vaadittu vähintään{' '}
                                            {eventLength * 5} tuntia
                                        </Typography>
                                        {eventLength * 5 - hours > 0 ? (
                                            <Typography variant="body1">
                                                Jäljellä {eventLength * 5 - hours} tuntia lisättäväksi
                                            </Typography>
                                        ) : (
                                            <Typography variant="body1">
                                                Kansanopistotunteja on riittävästi
                                            </Typography>
                                        )}
                                    </>
                                )}
                                {(!startTime || !endTime) && (
                                    <Typography>
                                        Et ole vielä määritellyt tapahtuman aloitus- tai lopetusaikaa
                                    </Typography>
                                )}
                            </Grid>
                        )}

                        {/* Kohdeyleisö */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">
                                Tapahtuman kohdeyleisö{' '}
                                <HelpIconWithTooltip
                                    text="Kenelle tapahtuma on suunnattu?"
                                    iconSize={20}
                                    tooltipFontSize="16px"
                                />
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormSelectMultiple
                                fieldName="kohdeyleiso"
                                options={audiences}
                                label="Kohdeyleisö"
                            />
                        </Grid>

                        {/* Aloitus & lopetus */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman ajankohta</Typography>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={8}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: { xs: 2, md: 3 },
                                justifyContent: { md: 'space-between' }, // Original layout on md+
                            }}
                        >
                            <Typography
                                width={{ xs: 'auto', md: '10%' }}
                                marginY="auto"
                                variant="subtitle1"
                            >
                                Aloitus
                            </Typography>
                            <FormDatePicker fieldName="aloitus.pvm" />
                            <FormTimePicker fieldName="aloitus.aika" />
                        </Grid>
                        <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }} />
                        <Grid
                            item
                            xs={12}
                            md={8}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: { xs: 2, md: 3 },
                                justifyContent: { md: 'space-between' }, // Original layout on md+
                            }}
                        >
                            <Typography
                                width={{ xs: 'auto', md: '10%' }}
                                marginY="auto"
                                variant="subtitle1"
                            >
                                Lopetus
                            </Typography>
                            <FormDatePicker fieldName="lopetus.pvm" />
                            <FormTimePicker fieldName="lopetus.aika" />
                        </Grid>

                        {/* Paikka */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman paikka</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Places fieldName="paikka" centers={centers} places={placesArray} />
                        </Grid>

                        {/* Vastuuhenkilöt */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman vastuuhenkilöt</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Vastuuhenkilöt fieldName="vastuuhenkilot" people={people} />
                        </Grid>

                        {/* Mukana olevat */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">
                                Tapahtumassa mukanaolevat henkilöt
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <MukanaOlevat
                                fieldName="mukanaolevat"
                                subfields={[
                                    { name: 'henkilo', options: people, xs: 5 },
                                    { name: 'rooli', options: roles, xs: 4 },
                                    { name: 'julkinen', options: ['Kyllä', 'Ei'], xs: 3 },
                                ]}
                                emptyMsg="Tapahtumalla ei vielä ole mukanaolevia henkilöitä"
                            />
                        </Grid>

                        {/* Henkilöiden luominen */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Luo henkilöitä</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            {henkiloSuccessMessage && (
                                <Box my={2}>
                                    <Alert
                                        severity="success"
                                        onClose={() => setHenkiloSuccessMessage('')}
                                    >
                                        {henkiloSuccessMessage}
                                    </Alert>
                                </Box>
                            )}
                            {henkiloErrorMessage && (
                                <Box my={2}>
                                    <Alert
                                        severity="error"
                                        onClose={() => setHenkiloErrorMessage('')}
                                    >
                                        {henkiloErrorMessage}
                                    </Alert>
                                </Box>
                            )}
                            <HenkilotAddForm
                                onError={setHenkiloErrorMessage}
                                onSuccess={setHenkiloSuccessMessage}
                            />
                        </Grid>

                        {/* Yhteistyötahot */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman yhteistyötahot</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormSelectMultiple
                                fieldName="yhteistyotahot"
                                options={partners}
                                label="Yhteistyötahot"
                            />
                            {successMessage && (
                                <Box my={2}>
                                    <Alert
                                        severity="success"
                                        onClose={() => setSuccessMessage('')}
                                    >
                                        {successMessage}
                                    </Alert>
                                </Box>
                            )}
                            {errorMessage && (
                                <Box my={2}>
                                    <Alert severity="error" onClose={() => setErrorMessage('')}>
                                        {errorMessage}
                                    </Alert>
                                </Box>
                            )}
                            <YhteistyotahotForm
                                onError={setErrorMessage}
                                onSuccess={setSuccessMessage}
                            />
                        </Grid>

                        {/* Kuvaus */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman kuvaus *</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <RichTextEditor
                                fieldName="kuvaus"
                                defaultValue={kuvausValue}
                                onChange={handleKuvausChange}
                            />
                        </Grid>

                        {/* Ohjelma */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman ohjelma *</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Schedule
                                kansanopisto={kansanopistokurssi}
                                fieldName="ohjelma"
                                setHours={setHours}
                            />
                        </Grid>

                        {/* Lisätiedot */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman lisätiedot</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <RichTextEditor
                                fieldName="lisatiedot"
                                defaultValue={lisatiedotValue}
                                onChange={handleLisatiedotChange}
                            />
                        </Grid>

                        {/* Kuvat */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman kuvat</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <ImageUploader fieldName="kuvat" />
                        </Grid>

                        {/* Musiikki */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">Tapahtuman musiikki</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <MusicListField
                                fieldName="musiikki"
                                subfields={['kappale', 'saveltaja', 'sanoittaja', 'sovittaja', 'artisti']}
                                emptyMsg="Tapahtumalla ei vielä ole musiikkia"
                            />
                        </Grid>

                        {/* Lyytilinkit */}
                        <Grid item xs={12} md={4} sx={{ my: { xs: 1, md: 'auto' }, mt: { md: 1 } }}>
                            <Typography variant="h6">Lyytilinkit</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormTextField
                                        fieldName="lyytilinkit.ilmoittautumislinkki"
                                        label="Ilmoittautumislinkki"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormTextField
                                        fieldName="lyytilinkit.eta_ilmoittautumislinkki"
                                        label="Etäosallistujan ilmoittautumislinkki"
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Muistiinpanot */}
                        <Grid item xs={12} md={4}>
                            <Typography variant="h6">
                                Tapahtuman <br /> muistiinpanot
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormNotes fieldName="muistiinpanot" />
                        </Grid>

                        {/* Lähetys */}
                        <Grid item xs={12} md={4} sx={{ my: { xs: 1, md: 'auto' } }}>
                            <Typography variant="h6">Tapahtuman lähetys</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormCheckboxGroup
                                fieldName="lahetys"
                                defaultValue={['ei lahetysta']}
                                options={[
                                    'ei lahetysta',
                                    'striimataan',
                                    'radioidaan (radio dei)',
                                    'radioidaan (radio316)',
                                ]}
                            />
                        </Grid>

                        {/* Tila */}
                        <Grid item xs={12} md={4} sx={{ my: { xs: 1, md: 'auto' } }}>
                            <Typography variant="h6">Tapahtuman tila</Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <FormRadioGroup
                                fieldName="status"
                                options={['peruutettu', 'julkaistu', 'luonnos', 'esitiedot']}
                            />
                        </Grid>

                        {/* Submit */}
                        <Grid item xs={12} sx={{ pt: 3, textAlign: 'center' }}>
                            <Button
                                type="submit"
                                form="event-form"
                                variant="contained"
                                disabled={submitting || submitted}
                                sx={{
                                    width: { xs: '100%', md: '50%' }, // Original width: 50% on md+
                                    backgroundColor: 'taphaBlue.dark',
                                }}
                            >
                                {!submitting ? (
                                    isEditing ? 'Tallenna muutokset' : 'Tallenna tapahtuma'
                                ) : (
                                    <CircularProgress
                                        thickness={5}
                                        size={25}
                                        sx={{ color: 'white' }}
                                        value={90}
                                    />
                                )}
                            </Button>
                        </Grid>

                        <MessageSnackbar
                            showSnackbar={submitted || error}
                            severity={submitted ? 'success' : 'error'}
                            msg={message}
                        />
                        {showWarning && (
                            <Grid item xs={12} sx={{ pb: { xs: 4, md: 8 }, pt: 2 }}>
                                <Box display="flex" justifyContent="center">
                                    <Alert
                                        severity="warning"
                                        onClose={() => setShowWarning(false)}
                                        sx={{ width: { xs: '100%', md: 'auto' } }}
                                    >
                                        {warningMessage}
                                    </Alert>
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </FormProvider>
        </Container>
    );
};

export default EventForm;